import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Loader2, Lock, ArrowLeft, TrendingUp, Inbox, DollarSign, Mail, Send, Check, HelpCircle } from "lucide-react";
import ConversionTimeline from "../components/ConversionTimeline";

const CURRENCY_SYMBOL = { USD: "$", HKD: "HK$", TWD: "NT$", CNY: "¥" };
const LANG_LABEL = { en: "English", "zh-HK": "繁中·港", "zh-TW": "繁中·台", "zh-CN": "简中" };
const FAQ_LABEL = {
  is_my_letter_private: "Is my letter private?",
  what_if_email_changes: "What if I change my email a year from now?",
  can_i_delete_letter: "Can I delete my letter if I regret writing it?",
  no_confirmation_email: "Why haven't I received a confirmation email?",
  why_do_i_pay: "Why do I pay?",
};

export default function AdminPage() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => sessionStorage.getItem("mp-admin-token") || "");
  const [authed, setAuthed] = useState(!!sessionStorage.getItem("mp-admin-token"));
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rescuingId, setRescuingId] = useState(null);
  const [rescuedRows, setRescuedRows] = useState([]); // capsules already rescued this session
  const rescuedIds = React.useMemo(() => new Set(rescuedRows.map((r) => r.id)), [rescuedRows]);

  const rescueDeliver = async (capsuleId) => {
    const ok = window.confirm(
      "Force-deliver this sealed letter to the recipient right now? This is irreversible."
    );
    if (!ok) return;
    // Snapshot the row so we can show the "delivered" badge after the queue refresh removes it.
    const row = stats?.queue?.find((c) => c.id === capsuleId);
    setRescuingId(capsuleId);
    try {
      await api.post(
        `/admin/rescue-deliver/${capsuleId}`,
        {},
        { headers: { "X-Admin-Token": token } }
      );
      if (row) setRescuedRows((prev) => [{ ...row, _rescued: true }, ...prev]);
      await loadStats(token);
    } catch (e) {
      alert(e.response?.data?.detail || String(e));
    } finally {
      setRescuingId(null);
    }
  };

  const loadStats = async (t) => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/admin/stats", { headers: { "X-Admin-Token": t } });
      setStats(res.data);
      setAuthed(true);
    } catch (e) {
      if (e.response?.status === 401) {
        setError("Wrong token.");
        sessionStorage.removeItem("mp-admin-token");
        setAuthed(false);
      } else {
        setError(e.response?.data?.detail || String(e));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const stored = sessionStorage.getItem("mp-admin-token");
    if (!stored) return;
    loadStats(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogin = async (e) => {
    e.preventDefault();
    if (!token) return;
    try {
      await api.post("/admin/auth", { token });
      sessionStorage.setItem("mp-admin-token", token);
      setAuthed(true);
      await loadStats(token);
    } catch (err) {
      setError(err.response?.status === 401 ? "Wrong token." : String(err));
    }
  };

  const onLogout = () => {
    sessionStorage.removeItem("mp-admin-token");
    setToken("");
    setAuthed(false);
    setStats(null);
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <form
          onSubmit={onLogin}
          className="w-full max-w-md bg-[var(--bg-paper-deep)]/60 p-10 border border-[var(--rule)]"
          data-testid="admin-login-form"
        >
          <div className="flex items-center gap-3 mb-8">
            <Lock className="text-[var(--wax)]" size={20} />
            <h1 className="font-display text-2xl tracking-tight">Postmaster&apos;s office</h1>
          </div>
          <label className="block text-[11px] uppercase tracking-[0.3em] text-[var(--ink-soft)] mb-3">
            Admin token
          </label>
          <input
            type="password"
            data-testid="admin-token-input"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="••••••••"
            className="focus-line w-full text-[18px] py-3 mb-6 text-[var(--ink)]"
          />
          {error && <div className="text-[var(--wax-deep)] text-sm italic mb-4" data-testid="admin-error">{error}</div>}
          <button
            type="submit"
            data-testid="admin-login-btn"
            className="btn-wax w-full py-3 text-[12px] uppercase tracking-[0.22em]"
          >
            Enter
          </button>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-6 text-[12px] uppercase tracking-[0.22em] text-[var(--ink-soft)] hover:text-[var(--wax)] inline-flex items-center gap-2"
          >
            <ArrowLeft size={12} /> Back home
          </button>
        </form>
      </div>
    );
  }

  if (loading || !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--ink-soft)]">
        <Loader2 className="animate-spin mr-3" /> Loading the books...
      </div>
    );
  }

  const m = stats.metrics;
  const conversionTone =
    m.conversion_rate >= 3 ? "var(--wax-deep)" : m.conversion_rate >= 1 ? "var(--wax)" : "var(--ink-soft)";

  return (
    <div className="min-h-screen px-6 py-16 md:py-20" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-12">
          <div>
            <p className="text-[11px] uppercase tracking-[0.4em] text-[var(--wax)] mb-3">Postmaster&apos;s office</p>
            <h1 className="font-display text-4xl tracking-tight">The books, tonight.</h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => loadStats(token)}
              className="text-[12px] uppercase tracking-[0.22em] text-[var(--ink-soft)] hover:text-[var(--wax)]"
              data-testid="admin-refresh-btn"
            >
              Refresh
            </button>
            <button
              onClick={onLogout}
              className="text-[12px] uppercase tracking-[0.22em] text-[var(--ink-soft)] hover:text-[var(--wax)]"
              data-testid="admin-logout-btn"
            >
              Sign out
            </button>
          </div>
        </div>

        {/* 3 core metrics */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Revenue */}
          <Card icon={<DollarSign size={16} />} label="Total revenue" testid="metric-revenue">
            {stats.revenue_by_currency.length === 0 ? (
              <div className="font-display text-3xl text-[var(--ink-mute)]">—</div>
            ) : (
              <div className="space-y-3">
                {stats.revenue_by_currency.map((r) => (
                  <div key={r.currency}>
                    <div className="flex items-baseline justify-between">
                      <div className="font-display text-3xl text-[var(--ink)]">
                        {CURRENCY_SYMBOL[r.currency] || ""}{r.total.toLocaleString()}
                      </div>
                      <div className="text-[11px] tracking-[0.2em] text-[var(--ink-soft)] uppercase">
                        {r.currency} · {r.count} paid
                      </div>
                    </div>
                    {r.mocked_count > 0 && (
                      <div
                        className="mt-1 text-[11px] italic text-[var(--ink-mute)] flex items-center justify-between gap-3"
                        data-testid={`mocked-revenue-${r.currency}`}
                      >
                        <span>
                          incl. mocked: {CURRENCY_SYMBOL[r.currency] || ""}
                          {r.mocked_total.toLocaleString()} · {r.mocked_count}
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--wax)] whitespace-nowrap">
                          pre-resend
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Conversion */}
          <Card icon={<TrendingUp size={16} />} label="Conversion rate" testid="metric-conversion">
            <div className="font-display text-5xl" style={{ color: conversionTone }} data-testid="conversion-value">
              {m.conversion_rate.toFixed(2)}%
            </div>
            <div className="mt-3 text-[12px] text-[var(--ink-soft)] italic leading-[1.7]">
              {m.total_sealed} sealed of {m.total_visits} visitors
              <br />
              {m.conversion_rate >= 3
                ? "Your copy is on fire — keep this hero."
                : m.conversion_rate >= 1
                ? "Healthy. Try A/B-ing the subtitle."
                : "Cold — consider sharpening the headline."}
            </div>
          </Card>

          {/* Queue */}
          <Card icon={<Inbox size={16} />} label="Sleeping memories" testid="metric-queue">
            <div className="font-display text-5xl text-[var(--ink)]" data-testid="queue-value">{m.queue_count}</div>
            <div className="mt-3 text-[12px] text-[var(--ink-soft)] italic leading-[1.7]">
              waiting in the dark
              <br />
              <span className="text-[var(--ink)]">{m.total_delivered}</span> already returned
              <span className="mx-2">·</span>
              <span className="text-[var(--ink)]">{m.total_pending}</span> never paid
            </div>
          </Card>
        </div>

        {/* Conversion by language */}
        <div className="mt-14">
          <h2 className="font-display text-xl tracking-tight mb-2">Conversion by language</h2>
          <p className="text-[13px] text-[var(--ink-soft)] italic mb-6">
            Where to send the next dollar of ad spend.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {stats.conversion_by_language.map((row) => {
              const tone =
                row.rate >= 3 ? "var(--wax-deep)" : row.rate >= 1 ? "var(--wax)" : "var(--ink-mute)";
              return (
                <div
                  key={row.language}
                  data-testid={`conv-by-lang-${row.language}`}
                  className="border border-[var(--rule)] p-5 bg-[var(--bg-paper-deep)]/40"
                >
                  <div className="text-[11px] uppercase tracking-[0.25em] text-[var(--ink-soft)]">
                    {LANG_LABEL[row.language] || row.language}
                  </div>
                  <div
                    className="font-display text-4xl mt-2"
                    style={{ color: tone }}
                    data-testid={`conv-rate-${row.language}`}
                  >
                    {row.rate.toFixed(2)}%
                  </div>
                  <div className="mt-2 text-[12px] text-[var(--ink-soft)] italic">
                    {row.visits === 0 && row.sealed === 0 ? "no data yet" : `${row.sealed} of ${row.visits}`}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Conversion timeline (7-day) */}
        <div className="mt-14">
          <ConversionTimeline token={token} />
        </div>

        {/* By-language paid breakdown (totals) */}
        <div className="mt-12">
          <h2 className="font-display text-xl tracking-tight mb-5">Paid letters · by language</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {["en", "zh-HK", "zh-TW", "zh-CN"].map((code) => (
              <div key={code} className="border border-[var(--rule)] p-5 bg-[var(--bg-paper-deep)]/40">
                <div className="text-[11px] uppercase tracking-[0.25em] text-[var(--ink-soft)]">{LANG_LABEL[code]}</div>
                <div className="font-display text-3xl mt-2" data-testid={`lang-count-${code}`}>
                  {stats.by_language[code] || 0}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ open-rate analytics (which fear dominates pre-purchase) */}
        <div className="mt-14" data-testid="faq-analytics">
          <h2 className="font-display text-xl tracking-tight mb-5 flex items-center gap-2">
            <HelpCircle size={16} /> Which question dominates the doubt
          </h2>
          {(!stats.faq_stats || stats.faq_stats.length === 0) ? (
            <div className="text-[var(--ink-soft)] italic py-6 text-[14px]">
              No FAQ opens recorded yet. Once visitors start exploring the accordion above the checkout, the ranking will appear here.
            </div>
          ) : (
            <div className="border border-[var(--rule)] bg-[var(--bg-paper-deep)]/40">
              {stats.faq_stats.map((row, idx) => {
                const label = FAQ_LABEL[row.question_key] || row.question_key;
                const widthPct = Math.max(2, row.share_pct);
                return (
                  <div
                    key={row.question_key}
                    data-testid={`faq-stat-row-${row.question_key}`}
                    className="grid grid-cols-12 gap-3 px-5 py-4 border-b border-[var(--rule)] last:border-b-0 items-center text-[14px]"
                  >
                    <div className="col-span-1 text-[var(--ink-mute)] font-display text-[18px]">
                      {idx + 1}
                    </div>
                    <div className="col-span-6 text-[var(--ink)]">{label}</div>
                    <div className="col-span-3 relative h-2 bg-[var(--rule)]/40 overflow-hidden">
                      <div
                        className="absolute inset-y-0 left-0 bg-[var(--wax)]"
                        style={{ width: `${widthPct}%` }}
                      />
                    </div>
                    <div className="col-span-1 text-right text-[var(--ink)] font-display" data-testid={`faq-opens-${row.question_key}`}>
                      {row.opens}
                    </div>
                    <div className="col-span-1 text-right text-[var(--ink-soft)] text-[12px]">
                      {row.share_pct}%
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Delivery queue */}
        <div className="mt-14">
          <h2 className="font-display text-xl tracking-tight mb-5 flex items-center gap-2">
            <Mail size={16} /> Next to wake up
          </h2>
          {stats.queue.length === 0 ? (
            <div className="text-[var(--ink-soft)] italic py-8">No sealed letters yet. The mailbox is quiet.</div>
          ) : (
            <div className="border-t border-[var(--rule)]">
              {/* Merge any rows just rescued this session so the 'delivered' badge is visible */}
              {[...rescuedRows, ...stats.queue.filter((c) => !rescuedIds.has(c.id))].map((c) => (
                <div
                  key={c.id}
                  data-testid={`queue-row-${c.id}`}
                  className="grid grid-cols-12 gap-3 py-5 border-b border-[var(--rule)] items-center text-[15px]"
                >
                  <div className="col-span-3 font-display text-[var(--ink)] truncate flex items-center gap-2">
                    {c.user_name}
                    {c.is_mocked_paid && (
                      <span
                        data-testid={`queue-mocked-${c.id}`}
                        className="text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 border border-[var(--wax)] text-[var(--wax)] not-italic"
                      >
                        mocked
                      </span>
                    )}
                    {c.promo_code && (
                      <span
                        data-testid={`queue-promo-${c.id}`}
                        title={c.promo_code}
                        className="text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 border border-[var(--wax)] bg-[var(--wax)]/[0.06] text-[var(--wax)] not-italic whitespace-nowrap"
                      >
                        promo · {c.promo_code}
                      </span>
                    )}
                  </div>
                  <div className="col-span-3 text-[var(--ink-soft)] truncate">{c.user_email}</div>
                  <div className="col-span-1 text-[var(--ink-soft)] text-[12px] uppercase tracking-[0.15em]">
                    {LANG_LABEL[c.language_code] || c.language_code}
                  </div>
                  <div className="col-span-2 text-[var(--ink)]">
                    {CURRENCY_SYMBOL[c.currency] || ""}{c.amount}
                  </div>
                  <div className="col-span-1 text-[var(--ink-soft)] text-right text-[13px]">
                    {new Date(c.delivery_date).toLocaleDateString()}
                  </div>
                  <div className="col-span-2 text-right">
                    {rescuedIds.has(c.id) ? (
                      <span
                        data-testid={`rescued-badge-${c.id}`}
                        className="text-[11px] uppercase tracking-[0.22em] text-[var(--wax)] inline-flex items-center gap-1"
                      >
                        <Check size={12} /> delivered
                      </span>
                    ) : (
                      <button
                        onClick={() => rescueDeliver(c.id)}
                        disabled={rescuingId === c.id}
                        data-testid={`rescue-btn-${c.id}`}
                        className="text-[11px] uppercase tracking-[0.22em] text-[var(--ink-soft)] hover:text-[var(--wax)] disabled:opacity-50 inline-flex items-center gap-1"
                      >
                        {rescuingId === c.id ? (
                          <Loader2 className="animate-spin" size={12} />
                        ) : (
                          <Send size={12} />
                        )}{" "}
                        deliver now
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-16 text-center">
          <button
            onClick={() => navigate("/")}
            className="text-[12px] uppercase tracking-[0.22em] text-[var(--ink-soft)] hover:text-[var(--wax)] inline-flex items-center gap-2"
            data-testid="admin-back-home"
          >
            <ArrowLeft size={12} /> Back to the front desk
          </button>
        </div>
      </div>
    </div>
  );
}

const Card = ({ icon, label, children, testid }) => (
  <div className="border border-[var(--rule)] bg-[var(--bg-paper-deep)]/40 p-7" data-testid={testid}>
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[var(--wax)] mb-5">
      {icon}
      {label}
    </div>
    {children}
  </div>
);
